import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, ShoppingBag, X } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { Wordmark } from "./Wordmark";
import { EASE } from "./motion";

export const NAV = [
  { to: "/", label: "Home", id: "home" },
  { to: "/our-story", label: "Our Story", id: "our-story" },
  { to: "/shop", label: "Shop", id: "shop" },
  { to: "/wholesale", label: "Wholesale", id: "wholesale" },
  { to: "/contact", label: "Contact", id: "contact" },
];

export const Header = () => {
  const { count, setOpen } = useCart();
  const [menu, setMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setMenu(false);
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header data-testid="header-nav" className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow] duration-500 ${scrolled || menu ? "bg-dano-cream/85 backdrop-blur-xl shadow-[0_1px_0_rgba(26,23,20,0.12)]" : "bg-transparent"}`}>
      <div className="mx-auto flex h-16 sm:h-20 max-w-[1440px] items-center justify-between px-5 sm:px-8 lg:px-12">
        <Link to="/" data-testid="header-logo-link" aria-label="DanoBars home" className="text-[15px] sm:text-[17px]">
          <Wordmark />
        </Link>
        <nav className="hidden md:flex items-center gap-1">
          {NAV.slice(1).map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-link-${n.id}`} className={({ isActive }) => `nav-link relative px-4 py-2 font-cond text-base uppercase tracking-[0.16em] ${isActive ? "text-dano-red" : "text-dano-ink"}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <button onClick={() => setOpen(true)} data-testid="cart-trigger-button" aria-label="Open cart" className="btn-press relative inline-flex h-11 items-center gap-2 rounded-full border-2 border-dano-ink px-4 font-cond uppercase tracking-widest text-dano-ink hover:bg-dano-ink hover:text-dano-cream transition-colors duration-300">
            <ShoppingBag className="h-4 w-4" />
            <span className="hidden sm:inline">Cart</span>
            <span data-testid="cart-count" className="grid h-5 min-w-5 place-items-center rounded-full bg-dano-red px-1 text-xs text-dano-cream">{count}</span>
          </button>
          <button onClick={() => setMenu((m) => !m)} data-testid="mobile-menu-toggle" aria-label="Menu" className="md:hidden grid h-11 w-11 place-items-center rounded-full bg-dano-ink text-dano-cream">
            {menu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {menu && (
          <motion.nav initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} transition={{ duration: 0.5, ease: EASE }} className="md:hidden overflow-hidden border-t border-dano-ink/10" data-testid="mobile-menu">
            <div className="flex flex-col px-5 py-6">
              {NAV.map((n, i) => (
                <motion.div key={n.to} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i, ease: EASE }}>
                  <NavLink to={n.to} data-testid={`mobile-nav-link-${n.id}`} className={({ isActive }) => `block py-2 font-wide text-3xl uppercase ${isActive ? "text-dano-red" : "text-dano-ink"}`}>
                    {n.label}
                  </NavLink>
                </motion.div>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
